import { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import {
  Users, FileText, ListOrdered, MapPin, GraduationCap, ClipboardList,
  CreditCard, ArrowRight, Upload, Award, Trash2, Sparkles, TrendingUp, AlertTriangle,
  Calendar, StickyNote, Download,
} from "lucide-react";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const Stat = ({ label, value, icon: Icon, accent = "navy", testid, hint }) => {
  const accentClasses =
    accent === "gold"
      ? "from-brand-gold/30 to-brand-gold/10 text-brand-navy"
      : accent === "rose"
      ? "from-rose-500/30 to-rose-500/5 text-rose-700"
      : accent === "emerald"
      ? "from-emerald-400/30 to-emerald-400/5 text-emerald-700"
      : "from-brand-navy/15 to-brand-navy/5 text-brand-navy";
  return (
    <Card className="card-surface" data-testid={testid}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
            <p className="text-3xl font-heading font-bold text-slate-900 mt-1 tabular-nums">{value}</p>
            {hint && <p className="text-xs text-slate-500 mt-1 truncate">{hint}</p>}
          </div>
          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accentClasses} flex items-center justify-center shrink-0`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickAction = ({ label, icon: Icon, onClick, danger, testid, description }) => (
  <button
    onClick={onClick}
    className={`flex items-start gap-3 text-left p-4 rounded-xl border-2 transition shadow-sm hover:shadow-md
      ${danger
        ? "border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-800"
        : "border-slate-200 bg-white hover:border-brand-navy/40 hover:bg-brand-navy/5 text-slate-800"}`}
    data-testid={testid}
  >
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0
      ${danger ? "bg-rose-200/60 text-rose-700" : "bg-brand-gold/30 text-brand-navy"}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-heading font-bold text-sm">{label}</p>
      {description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{description}</p>}
    </div>
    <ArrowRight className="w-4 h-4 text-slate-400 mt-2.5 shrink-0" />
  </button>
);

const AdminOverview = ({ onNavigate }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followUps, setFollowUps] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [snapshotPeriod, setSnapshotPeriod] = useState("all_time");

  const handleDownloadSnapshot = async () => {
    setDownloading(true);
    try {
      const r = await fetch(`${API}/admin/overview/snapshot.pdf?period=${snapshotPeriod}`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to generate PDF");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aspiritech-snapshot-${snapshotPeriod}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Snapshot PDF downloaded");
    } catch (e) {
      toast.error(e.message || "Could not download snapshot");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/admin/overview`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    fetch(`${API}/admin/notes/follow-ups?horizon_days=7`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!cancelled) setFollowUps(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setFollowUps([]); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-8" data-testid="admin-overview">
      {/* Hero */}
      <div className="rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden"
           style={{ background: "linear-gradient(135deg, #0F1B3D 0%, #1E3A8A 60%, #2A4FA0 100%)" }}>
        <div className="relative max-w-2xl">
          <span className="text-brand-gold text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Admin Overview
          </span>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-2">Today at a glance</h2>
          <p className="text-white/80 mt-2 text-sm sm:text-base">
            Quick view of new enrollments, pending pipeline work, and waitlist demand.
            Use the sidebar to dive into any area.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              value={snapshotPeriod}
              onChange={(e) => setSnapshotPeriod(e.target.value)}
              className="rounded-lg bg-white/10 border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 [&>option]:text-slate-900"
              data-testid="snapshot-period-select"
            >
              <option value="all_time">All-time</option>
              <option value="this_year">This year</option>
              <option value="this_quarter">This quarter</option>
              <option value="last_30_days">Last 30 days</option>
            </select>
            <button
              onClick={handleDownloadSnapshot}
              disabled={downloading || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-gold px-4 py-2 text-sm font-bold text-brand-navy hover:bg-brand-gold/90 transition disabled:opacity-60"
              data-testid="download-snapshot-btn"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Preparing PDF…" : "Export Snapshot PDF"}
            </button>
          </div>
        </div>
      </div>

      {/* Headline metrics — the at-a-glance numbers admins see first */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="headline-metrics">
        <Stat
          label="Participants Served (All-Time)"
          value={loading ? "—" : (data?.headline?.participants_served_ever ?? 0)}
          icon={Users}
          accent="navy"
          testid="headline-served"
          hint="Everyone enrolled, ever"
        />
        <Stat
          label="Enrolled in Current Cohorts"
          value={loading ? "—" : (data?.headline?.enrolled_current_cohorts ?? 0)}
          icon={GraduationCap}
          accent="gold"
          testid="headline-current-cohorts"
          hint="Active students right now"
        />
        <Stat
          label="Certified Alumni"
          value={loading ? "—" : (data?.headline?.certified_alumni ?? 0)}
          icon={Award}
          accent="emerald"
          testid="headline-certified-alumni"
          hint="Completed & certified"
        />
        <Card className="card-surface" data-testid="headline-waitlist">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">On Waiting List</p>
                <p className="text-3xl font-heading font-bold text-slate-900 mt-1 tabular-nums">
                  {loading ? "—" : (data?.headline?.waitlist_total ?? 0)}
                </p>
              </div>
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-navy/15 to-brand-navy/5 text-brand-navy flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Top States</p>
              {loading ? (
                <p className="text-xs text-slate-400">Loading…</p>
              ) : (data?.headline?.waitlist_top_states?.length ? (
                <div className="flex flex-wrap gap-1.5" data-testid="headline-top-states">
                  {data.headline.waitlist_top_states.map((s) => (
                    <span
                      key={s.code}
                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-brand-navy/10 text-brand-navy border border-brand-navy/15 font-medium"
                      data-testid={`top-state-${s.code}`}
                      title={s.name}
                    >
                      {s.code} <span className="tabular-nums text-slate-500">{s.count}</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">No waitlist entries yet</p>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          label="New Enrollments Today"
          value={loading ? "—" : (data?.enrollments_today ?? 0)}
          icon={TrendingUp}
          accent="gold"
          testid="kpi-enrollments-today"
          hint={loading ? "Loading…" : `${data?.approved_this_week ?? 0} approved this week`}
        />
        <Stat
          label="Pending Pipeline"
          value={loading ? "—" : (data?.pending_pipeline ?? 0)}
          icon={ListOrdered}
          accent="navy"
          testid="kpi-pending-pipeline"
          hint="Awaiting review or docs"
        />
        <Stat
          label="Program Waitlist"
          value={loading ? "—" : (data?.program_waitlist ?? 0)}
          icon={ClipboardList}
          accent="navy"
          testid="kpi-program-waitlist"
        />
        <Stat
          label="State Demand"
          value={loading ? "—" : (data?.state_waitlist?.total ?? 0)}
          icon={MapPin}
          accent="navy"
          testid="kpi-state-waitlist"
          hint={
            loading
              ? "Loading…"
              : `${data?.state_waitlist?.priority ?? 0} priority · ${data?.state_waitlist?.general ?? 0} general`
          }
        />
      </div>

      {/* Secondary stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          label="Total Users"
          value={loading ? "—" : (data?.total_users ?? 0)}
          icon={Users}
          accent="navy"
          testid="kpi-total-users"
        />
        <Stat
          label="Alumni"
          value={loading ? "—" : (data?.total_alumni ?? 0)}
          icon={GraduationCap}
          accent="emerald"
          testid="kpi-total-alumni"
        />
        <Stat
          label="Unpaid Apps"
          value={loading ? "—" : (data?.unpaid_applications ?? 0)}
          icon={CreditCard}
          accent={data?.unpaid_applications ? "rose" : "navy"}
          testid="kpi-unpaid-applications"
          hint={data?.unpaid_applications ? "May need follow-up" : "All caught up"}
        />
        <Stat
          label="Approved This Week"
          value={loading ? "—" : (data?.approved_this_week ?? 0)}
          icon={Award}
          accent="emerald"
          testid="kpi-approved-this-week"
        />
      </div>

      {/* Quick actions — filtered to current role */}
      <div>
        <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction
            label="Review Pipeline"
            icon={ListOrdered}
            description="Approve, waitlist, or follow up on applicants."
            onClick={() => onNavigate("pipeline")}
            testid="qa-pipeline"
          />
          {isAdmin && (
            <QuickAction
              label="Bulk Import"
              icon={Upload}
              description="Upload an Excel/CSV of new participants."
              onClick={() => onNavigate("bulk-import")}
              testid="qa-bulk-import"
            />
          )}
          {isAdmin && (
            <QuickAction
              label="State Demand"
              icon={MapPin}
              description="See where the next state should open."
              onClick={() => onNavigate("state-waitlist")}
              testid="qa-state-waitlist"
            />
          )}
          <QuickAction
            label="Welcome Packets"
            icon={GraduationCap}
            description="Edit per-program welcome packets."
            onClick={() => onNavigate("welcome-pack")}
            testid="qa-welcome-pack"
          />
          {isAdmin && (
            <QuickAction
              label="Maintenance"
              icon={Trash2}
              description="Purge demo / test data. Use with care."
              onClick={() => onNavigate("maintenance")}
              danger
              testid="qa-maintenance"
            />
          )}
        </div>
      </div>

      {data?.unpaid_applications > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4 pb-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-heading font-bold text-amber-900 text-sm">
                {data.unpaid_applications} {data.unpaid_applications === 1 ? "application is" : "applications are"} unpaid
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                Open the Pipeline to follow up — the $99 fee may be blocking enrollment.
              </p>
            </div>
            <button
              onClick={() => onNavigate("pipeline")}
              className="text-xs px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition shrink-0 mt-1"
              data-testid="qa-review-unpaid"
            >
              Open Pipeline →
            </button>
          </CardContent>
        </Card>
      )}

      {/* Follow-ups due this week */}
      {followUps.length > 0 && (
        <Card className="card-surface" data-testid="follow-ups-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brand-navy" />
                <h3 className="font-heading font-bold text-slate-900">Follow-ups due this week</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-semibold">
                  {followUps.length}
                </span>
              </div>
              <button onClick={() => onNavigate("pipeline")} className="text-xs text-slate-500 hover:text-brand-navy">
                View pipeline →
              </button>
            </div>
            <ul className="divide-y divide-slate-100">
              {followUps.slice(0, 6).map((f) => {
                const due = f.follow_up_due;
                const today = new Date().toISOString().slice(0, 10);
                const overdue = due && due < today;
                return (
                  <li key={f.note_id} className="py-2 flex items-center gap-3" data-testid={`follow-up-${f.note_id}`}>
                    {f.participant_picture ? (
                      <img src={f.participant_picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-brand-navy text-white flex items-center justify-center text-xs font-bold">
                        {(f.participant_name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{f.participant_name || f.participant_email}</p>
                      <p className="text-xs text-slate-500 truncate">{f.body_text}</p>
                    </div>
                    <span className={`text-xs whitespace-nowrap px-2 py-0.5 rounded-full
                      ${overdue ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                      {overdue ? "Overdue" : "Due"} {due}
                    </span>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminOverview;
